import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Project } from '../types';
import { ChevronDownIcon, DocumentIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';

interface LeftPanelProps {
  projects: Project[];
  selectedProject: Project | null;
  onProjectSelect: (project: Project) => void;
  onTableSelect: (project: Project) => void;
  onTypeTableSelect: (project: Project, path: string) => void;
  onVorSelect: (project: Project) => void;
  onFileUpload: (files: File[]) => void;
  isLoading: boolean;
}

const LeftPanel: React.FC<LeftPanelProps> = ({
  projects,
  selectedProject,
  onProjectSelect,
  onTableSelect,
  onTypeTableSelect,
  onVorSelect,
  onFileUpload,
  isLoading,
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');
    if (pdfFiles.length > 0) {
      onFileUpload(pdfFiles);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
  });

  return (
    <div className="h-full flex flex-col">
      {/* Заголовок */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Проекты</h2>
      </div>

      {/* Зона загрузки */}
      <div className="p-4 border-b border-gray-200">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            {isDragActive
              ? 'Отпустите файлы здесь...'
              : 'Перетащите PDF файлы сюда или нажмите для выбора'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Поддерживаются только PDF файлы
          </p>
        </div>
      </div>

      {/* Список проектов */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Загрузка...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-8">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">Нет проектов</p>
            <p className="text-xs text-gray-500">Загрузите PDF файл для начала</p>
          </div>
        ) : (
          <div className="space-y-2">
            {(Array.isArray(projects) ? projects : []).map((project) => (
              <ProjectCard
                key={project.session_id}
                project={project}
                isSelected={selectedProject?.session_id === project.session_id}
                onSelect={() => onProjectSelect(project)}
                onTableSelect={() => onTableSelect(project)}
                onTypeTableSelect={(path) => onTypeTableSelect(project, path)}
                onVorSelect={() => onVorSelect(project)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ProjectCardProps {
  project: Project;
  isSelected: boolean;
  onSelect: () => void;
  onTableSelect: () => void;
  onTypeTableSelect: (path: string) => void;
  onVorSelect: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, isSelected, onSelect, onTableSelect, onTypeTableSelect, onVorSelect }) => {
  const [isExpanded, setIsExpanded] = React.useState(true); // По умолчанию раскрыто

  return (
    <div
      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {project.project_name}
          </h3>
          <p className="text-xs text-gray-500 truncate">
            {new Date(project.created_at).toLocaleString('ru-RU', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
          
          {/* Этап 1: Классификация типов */}
          {project.files.type_classification_table && !project.files.binding_table && project.files.status !== 'GENERATING_BINDING_TABLE' && (
             <div 
              className="flex items-center justify-between p-2 bg-yellow-50 rounded hover:bg-yellow-100 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if(project.files.type_classification_table?.path) {
                    onTypeTableSelect(project.files.type_classification_table.path);
                }
              }}
            >
              <div className="flex items-center space-x-2">
                <span className="text-xs text-yellow-800">1. Классификация типов</span>
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    🟡 Ожидает проверки
                  </span>
              </div>
            </div>
          )}

          {/* Этап 2: Таблица привязки */}
          {project.files.binding_table ? (
            <div 
              className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onTableSelect();
              }}
            >
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-700">2. Таблица привязки</span>
                {project.files.binding_table.is_corrected ? (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    🟢 Откорректирована
                  </span>
                ) : (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    🔵 Ожидает проверки
                  </span>
                )}
              </div>
            </div>
          ) : !project.files.type_classification_table && !project.files.binding_table && (
             <div className="flex items-center space-x-2 text-xs text-gray-500 p-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
              <span>Анализ... (Шаг 1/3)</span>
            </div>
          )}

          {/* Если идет генерация таблицы привязки, показываем статус */}
          {project.files.status === 'GENERATING_BINDING_TABLE' && (
            <div className="flex items-center space-x-2 text-xs text-gray-500 p-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
              <span>Создание таблицы привязки... (Шаг 2/3)</span>
            </div>
          )}
          
          {/* Этап 3: ВОР */}
          {project.files.vor && (
            <div 
              className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onVorSelect();
              }}
            >
              <span className="text-xs text-gray-700">3. ВОР</span>
              <span className="text-xs text-green-600">✓ Готов</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeftPanel; 